import React, { PureComponent } from "react";
import styled, { keyframes } from "styled-components";
import NumberFormat from "react-number-format";
import { Loader } from "react-loaders";

import { batchRequest, getRequest, postRequest, putRequest } from "../../../api/ApiService";
import { fetchTeamDecisions } from "../../../api/TeamDecisionsService";
import Auth from "../../../routes/Auth";
import Styled from "components/finance/style";

// Subtle on-card animations, one personality per lender risk tier.
// Slow durations (2.5-3.5s) so the motion stays in the background.
const piggyBob = keyframes`
  0%, 100% { transform: rotate(-2deg); }
  50%     { transform: rotate(2deg); }
`;

const coinDrop = keyframes`
  0%   { transform: translateY(-12px); opacity: 0; }
  20%  { opacity: 1; }
  60%  { transform: translateY(0); opacity: 1; }
  80%  { opacity: 1; }
  100% { transform: translateY(0); opacity: 0; }
`;

const briefcaseLift = keyframes`
  0%, 100% { transform: translateY(0); }
  50%     { transform: translateY(-2px); }
`;

const arrowGrow = keyframes`
  0%   { transform: scaleY(0.4); opacity: 0.4; }
  60%  { transform: scaleY(1);   opacity: 1; }
  100% { transform: scaleY(1);   opacity: 1; }
`;

const sharkSwim = keyframes`
  0%, 100% { transform: translateY(0) rotate(-1deg); }
  50%     { transform: translateY(-1.5px) rotate(1deg); }
`;

const sharkJaw = keyframes`
  0%, 100% { transform: translateY(0) scaleY(1); }
  10%     { transform: translateY(2px) scaleY(1.3); }
  20%     { transform: translateY(0) scaleY(1); }
`;

// 56×56 slot in the card, matches Styled.LenderLogo's footprint.
// overflow: hidden so the shark's tail / coin drop don't bleed past
// the rounded edge.
const LenderArtFrame = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: inherit;
`;

const PiggyBob = styled.g`
  transform-origin: 32px 38px;
  animation: ${piggyBob} 3.4s ease-in-out infinite;
`;

const PiggyCoin = styled.g`
  transform-origin: center;
  animation: ${coinDrop} 2.6s ease-in-out infinite;
`;

const BriefcaseLift = styled.g`
  transform-origin: center;
  animation: ${briefcaseLift} 2.6s ease-in-out infinite;
`;

const ArrowGrow = styled.g`
  transform-origin: 32px 50px;
  transform-box: fill-box;
  animation: ${arrowGrow} 2.8s ease-out infinite;
`;

const SharkSwim = styled.g`
  transform-origin: center;
  animation: ${sharkSwim} 3.2s ease-in-out infinite;
`;

const SharkJaw = styled.g`
  transform-origin: 32px 36px;
  transform-box: fill-box;
  animation: ${sharkJaw} 2.4s ease-in-out infinite;
`;

function PiggyBankArt() {
  return (
    <svg viewBox="0 0 64 64" width="48" height="48" xmlns="http://www.w3.org/2000/svg">
      {/* Falling coin (Prudent → savings) */}
      <PiggyCoin>
        <circle cx="32" cy="12" r="3.5" fill="#F2B900" stroke="#2DA548" strokeWidth="1" />
        <text x="32" y="14.2" fontSize="4" fontFamily="Mukta Vaani, Lato, sans-serif"
          fontWeight="700" textAnchor="middle" fill="#2DA548">$</text>
      </PiggyCoin>
      <PiggyBob>
        {/* Coin slot on the back */}
        <rect x="29" y="22" width="6" height="2" rx="0.5" fill="#1A6F30" />
        {/* Body */}
        <ellipse cx="32" cy="40" rx="20" ry="14" fill="#A6E4B7" stroke="#2DA548" strokeWidth="1.6" />
        {/* Ear */}
        <path d="M22 28 L 25 24 L 27 30 Z" fill="#A6E4B7" stroke="#2DA548" strokeWidth="1.2" />
        {/* Snout */}
        <ellipse cx="50" cy="40" rx="5" ry="4" fill="#86D69D" stroke="#2DA548" strokeWidth="1.2" />
        <circle cx="49" cy="39" r="0.8" fill="#1A6F30" />
        <circle cx="51" cy="40.5" r="0.8" fill="#1A6F30" />
        {/* Eye */}
        <circle cx="38" cy="36" r="1.5" fill="#1A6F30" />
        <circle cx="38.5" cy="35.5" r="0.5" fill="#FFFFFF" />
        {/* Legs */}
        <rect x="22" y="52" width="4" height="6" rx="1" fill="#86D69D" stroke="#2DA548" strokeWidth="1" />
        <rect x="38" y="52" width="4" height="6" rx="1" fill="#86D69D" stroke="#2DA548" strokeWidth="1" />
        {/* Tail */}
        <path d="M14 36 Q 10 36 12 32" fill="none" stroke="#2DA548" strokeWidth="1.6" strokeLinecap="round" />
      </PiggyBob>
    </svg>
  );
}

function BriefcaseChartArt() {
  return (
    <svg viewBox="0 0 64 64" width="48" height="48" xmlns="http://www.w3.org/2000/svg">
      <BriefcaseLift>
        {/* Handle */}
        <rect x="26" y="16" width="12" height="6" rx="2" fill="none" stroke="#B17F00" strokeWidth="1.6" />
        {/* Briefcase body */}
        <rect x="12" y="22" width="40" height="28" rx="3" fill="#FFE08A" stroke="#B17F00" strokeWidth="1.6" />
        {/* Clasp */}
        <rect x="29" y="30" width="6" height="4" rx="0.5" fill="#B17F00" />
        {/* Chart inside (small frame) */}
        <rect x="16" y="38" width="32" height="10" rx="1" fill="#FFFFFF" stroke="#B17F00" strokeWidth="1" />
        {/* Animated up-arrow line on the chart */}
        <ArrowGrow>
          <path d="M18 46 L 26 42 L 32 44 L 40 38" fill="none"
            stroke="#1A6F30" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M37 38 L 42 38 L 42 41" fill="none"
            stroke="#1A6F30" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </ArrowGrow>
      </BriefcaseLift>
    </svg>
  );
}

function SharkSuitArt() {
  return (
    <svg viewBox="0 0 64 64" width="48" height="48" xmlns="http://www.w3.org/2000/svg">
      <SharkSwim>
        {/* Suit body (black jacket, V-shape coming up) */}
        <path d="M10 60 L 18 44 L 32 48 L 46 44 L 54 60 Z"
          fill="#1A1F2B" stroke="#0D1119" strokeWidth="1" />
        {/* White shirt triangle */}
        <path d="M26 44 L 32 56 L 38 44 L 36 42 L 32 46 L 28 42 Z"
          fill="#FFFFFF" stroke="#2A3E55" strokeWidth="0.6" />
        {/* Red tie */}
        <path d="M30.5 42 L 33.5 42 L 34 46 L 32 56 L 30 46 Z"
          fill="#EC5E59" stroke="#A8403E" strokeWidth="0.5" />
        {/* Tie knot */}
        <path d="M30.5 42 L 33.5 42 L 33.2 44 L 30.8 44 Z" fill="#A8403E" />

        {/* Pectoral fins flaring outward */}
        <path d="M14 38 L 6 44 L 12 46 Z"
          fill="#6B8FAB" stroke="#2A3E55" strokeWidth="1" />
        <path d="M50 38 L 58 44 L 52 46 Z"
          fill="#6B8FAB" stroke="#2A3E55" strokeWidth="1" />

        {/* Shark head — front-facing rounded triangle (wider at top, tapered chin) */}
        <path d="M14 18 Q 32 6 50 18 L 46 38 Q 32 46 18 38 Z"
          fill="#8FB0CC" stroke="#2A3E55" strokeWidth="1.4" />
        {/* Lighter chin / underbelly */}
        <path d="M20 34 Q 32 42 44 34 L 42 38 Q 32 44 22 38 Z"
          fill="#D0DEEA" />

        {/* Dorsal fin peeking above head */}
        <path d="M30 10 L 32 4 L 34 10 Z"
          fill="#6B8FAB" stroke="#2A3E55" strokeWidth="1" />

        {/* Gill slits on cheeks */}
        <path d="M18 26 L 20 28 M 18 29 L 20 31" stroke="#2A3E55" strokeWidth="0.6" />
        <path d="M46 26 L 44 28 M 46 29 L 44 31" stroke="#2A3E55" strokeWidth="0.6" />

        {/* Two forward-facing eyes */}
        <ellipse cx="24" cy="22" rx="3" ry="2.6" fill="#FFFFFF" stroke="#2A3E55" strokeWidth="0.8" />
        <ellipse cx="40" cy="22" rx="3" ry="2.6" fill="#FFFFFF" stroke="#2A3E55" strokeWidth="0.8" />
        <circle cx="24.5" cy="22.2" r="1.3" fill="#1A1F2B" />
        <circle cx="40.5" cy="22.2" r="1.3" fill="#1A1F2B" />
        <circle cx="25" cy="21.6" r="0.4" fill="#FFFFFF" />
        <circle cx="41" cy="21.6" r="0.4" fill="#FFFFFF" />

        {/* Menacing brow line */}
        <path d="M20 18 L 27 19 M 37 19 L 44 18"
          fill="none" stroke="#2A3E55" strokeWidth="1.2" strokeLinecap="round" />

        {/* Mouth / jaw — front-facing chomp */}
        <SharkJaw>
          {/* Mouth opening (dark) */}
          <path d="M22 32 Q 32 36 42 32 L 42 36 Q 32 41 22 36 Z"
            fill="#1A1F2B" stroke="#2A3E55" strokeWidth="0.8" />
          {/* Upper teeth (pointing down) */}
          <path d="M23 33 L 25 36 L 27 33 L 29 36 L 31 33 L 33 36 L 35 33 L 37 36 L 39 33 L 41 36"
            fill="#FFFFFF" stroke="#FFFFFF" strokeWidth="0.5" />
          <path d="M23 33 L 25 36 L 27 33 L 29 36 L 31 33 L 33 36 L 35 33 L 37 36 L 39 33 L 41 36"
            fill="none" stroke="#2A3E55" strokeWidth="0.4" />
          {/* Lower teeth (pointing up) */}
          <path d="M24 39 L 26 36.5 L 28 39 L 30 36.5 L 32 39 L 34 36.5 L 36 39 L 38 36.5 L 40 39"
            fill="#FFFFFF" stroke="#FFFFFF" strokeWidth="0.5" />
          <path d="M24 39 L 26 36.5 L 28 39 L 30 36.5 L 32 39 L 34 36.5 L 36 39 L 38 36.5 L 40 39"
            fill="none" stroke="#2A3E55" strokeWidth="0.4" />
        </SharkJaw>
      </SharkSwim>
    </svg>
  );
}

function renderLenderArt(iconKey) {
  switch (iconKey) {
    case "piggy":     return <PiggyBankArt />;
    case "briefcase": return <BriefcaseChartArt />;
    case "shark":     return <SharkSuitArt />;
    default:          return null;
  }
}

// Static per-lender presentation. Keyed by lender name (case-insensitive
// prefix match). Falls back to a generic theme if no match.
const LENDER_PRESETS = [
  {
    match: /prudent/i,
    theme: "green",
    iconKey: "piggy",
    tagline: "Safe, Steady, Simple",
    risk: "Low Risk",
    description:
      "Your trusted community partner. Conservative lending with the lowest rates in the market.",
    fallbackDcLimit: 0.40
  },
  {
    match: /mezz/i,
    theme: "yellow",
    iconKey: "briefcase",
    tagline: "More Capital, More Ambition",
    risk: "Medium Risk",
    description:
      "Mezzanine financing for businesses ready to scale. Bridges the gap between conventional debt and equity.",
    fallbackDcLimit: 0.60
  },
  {
    match: /great white|shark/i,
    theme: "red",
    iconKey: "shark",
    tagline: "Big Money. Sharp Terms.",
    risk: "High Risk",
    description:
      "Maximum capital, maximum risk. Great White operates where traditional lenders won't.",
    fallbackDcLimit: 0.75
  }
];

const THEME_BY_INDEX = ["green", "yellow", "red"];
const ICON_BY_INDEX = ["piggy", "briefcase", "shark"];

function pickPreset(name, index) {
  const byName = LENDER_PRESETS.find(p => p.match.test(name || ""));
  if (byName) return byName;
  return {
    theme: THEME_BY_INDEX[index] || "green",
    iconKey: ICON_BY_INDEX[index] || null,
    tagline: "",
    risk: index === 0 ? "Low Risk" : index === 1 ? "Medium Risk" : "High Risk",
    description: ""
  };
}

const SEGMENT_COLORS = {
  green: "#2DA548",
  yellow: "#E5B636",
  red: "#EC5E59",
  black: "#1A1F2B"
};

const numberToM = val => {
  const n = Number(val) || 0;
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  return `${sign}$${(abs / 1000000).toFixed(abs >= 10000000 ? 2 : 3)}M`;
};

// Tween a numeric value from its previous to current prop over ~600ms
// using requestAnimationFrame + an easeOutCubic curve, then render via
// the provided format function. Used for the Max Loan, Available to
// Borrow, Outstanding Balance, Principal / Interest, and the status
// bar totals so they animate when a borrow/repay post succeeds.
class CountUp extends PureComponent {
  constructor(props) {
    super(props);
    this.state = { display: Number(props.value) || 0 };
  }

  componentDidUpdate(prevProps) {
    const next = Number(this.props.value) || 0;
    const prev = Number(prevProps.value) || 0;
    if (next !== prev) {
      this.animateTo(this.state.display, next);
    }
  }

  componentWillUnmount() {
    this._cancelled = true;
  }

  animateTo(from, to) {
    if (this._raf) cancelAnimationFrame(this._raf);
    const duration = 600;
    const start = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const step = (now) => {
      if (this._cancelled) return;
      const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - start;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const value = from + (to - from) * eased;
      this.setState({ display: t === 1 ? to : value });
      if (t < 1) {
        this._raf = requestAnimationFrame(step);
      }
    };
    this._raf = requestAnimationFrame(step);
  }

  render() {
    const { format } = this.props;
    return <span>{format(this.state.display)}</span>;
  }
}

const formatMoneyShort = (val, opts = {}) => {
  const n = Number(val) || 0;
  if (Math.abs(n) >= 1000) {
    return numberToM(n);
  }
  return (opts.prefix || "$") + n.toFixed(0);
};

const formatFullDollar = val => {
  const n = Math.round(Number(val) || 0);
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString('en-US')}`;
};

// Wrapper for the debt-to-capital indicator dot on the ratio bar.
// Positions itself at `left: N%` inside the bar and reveals a
// white-background tooltip with the exact ratio on hover — the always-
// visible "N.NN%" label was overlapping the "Your Current Debt /
// Capital Ratio" text so we moved the number into this hover reveal.
const RatioDotWrapper = styled.div`
  position: absolute;
  top: -3px;
  width: 12px;
  height: 12px;
  transform: translateX(-50%);

  &:hover > span.ratio-tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const RatioDotVisual = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(0, 27, 59, 0.94);
  border: 2px solid #fff;
`;

const RatioTooltip = styled.span`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(-10px);
  background: #fff;
  border: 1px solid rgba(0, 27, 59, 0.12);
  color: rgba(0, 27, 59, 0.94);
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  padding: 8px 14px;
  border-radius: 6px;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 27, 59, 0.15);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, visibility 0.15s ease;
  pointer-events: none;
  z-index: 10;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #fff;
  }
`;

// Hover tooltip wrapper for the "i" info icons on the Lender Offer row
// (Max Loan, D/C Limit). Reveals a dark 16px pill below the icon with a
// plain-language explanation. Pointer-events on the icon are killed so
// hover fires reliably on the wrapper.
const LenderInfoWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: help;

  &:hover > span.lender-info-tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const LenderInfoTooltip = styled.span`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(8px);
  background: rgba(0, 27, 59, 0.94);
  color: #fff;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  padding: 10px 14px;
  border-radius: 6px;
  width: 260px;
  text-align: left;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, visibility 0.15s ease;
  pointer-events: none;
  z-index: 20;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-bottom-color: rgba(0, 27, 59, 0.94);
  }
`;

class DebtDecisionsPage extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      lenders: [],
      totalExistingDebt: 0,
      netCapital: 0,
      debtToCapital: 0,
      cashBalance: 0,
      selectedLenderId: null,
      borrowAmount: '',
      repayAmount: '',
      playerId: 0,
      postingBorrow: false,
      postingRepay: false,
      noticeOpen: false,
      noticeTitle: '',
      noticeMessage: '',
      // Borrow confirmation modal — shows loan amount, interest rate,
      // and maturity before committing to the POST. See requestBorrow
      // / confirmBorrow / cancelBorrowConfirm below.
      borrowConfirmOpen: false,
      // Interest rate fetched on the fly from the debt_conditions
      // endpoint when the user clicks BORROW. Null while the request
      // is in flight (or unknown), a number when we have a value.
      borrowConfirmRate: null,
      borrowConfirmLoadingRate: false,
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchAll();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  fetchAll = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const { roundNumber } = this.props;

    Promise.all([
      getRequest(`${apiUrl}static/debt_clients/`),
      fetchTeamDecisions(roundNumber, "finance"),
      getRequest(`${apiUrl}team/debt_repayments/?round_number=${roundNumber}`)
        .then(r => r.body)
        .catch(() => []),
      getRequest(`${apiUrl}v2/finance/interest-payments/?round_number=${roundNumber}`)
        .then(r => r.body)
        .catch(() => ({ results: [] })),
      getRequest(`${apiUrl}team/players/`).then(r => r.body).catch(() => [])
    ])
      .then(([clientsRes, decisions, debtRepaymentsRes, interestPaymentsRes, playersRes]) => {
        if (!this._isMounted) return;

        const debtClients = clientsRes.body || [];
        const teamDebts = decisions.teamDebts || [];
        const financial = (decisions.financial_decisions && decisions.financial_decisions.team) || {};

        // Normalize repayments shapes (debt_repayments returns an array;
        // interest-payments returns {results: [...]} paginated)
        const debtRepayments = Array.isArray(debtRepaymentsRes)
          ? debtRepaymentsRes
          : (debtRepaymentsRes && debtRepaymentsRes.results) || [];
        const interestPayments = Array.isArray(interestPaymentsRes)
          ? interestPaymentsRes
          : (interestPaymentsRes && interestPaymentsRes.results) || [];

        // Find the current user's player record. interest-payments
        // POST requires player as a required FK.
        const players = Array.isArray(playersRes) ? playersRes : [];
        const userId = Number(Auth.getUserId());
        const player = players.find(p => p && p.user && Number(p.user.id) === userId);
        const playerId = player ? Number(player.id) : 0;

        const debtToCapital = Number(financial.debt_to_capital || 0);

        // Single bulk POST replaces the previous per-lender pair
        // (get_max_amount/ + get_debt_conditions/). Returns one entry
        // per (client, term) requested. We ask LT for every lender —
        // the Choose Your Lender cards display long-term offerings.
        const bulkBody = {
          requests: debtClients.map(client => ({
            client: client.id,
            term: 'LT',
            amount: 0,
          })),
        };

        postRequest(`${apiUrl}static/debt_clients/bulk/`).send(bulkBody)
          .then(r => Array.isArray(r.body) ? r.body : [])
          .catch(() => [])
          .then(bulkResponse => {
          if (!this._isMounted) return;

          // Index the bulk response by client_id for O(1) lookup. Each
          // entry: { client, term, max_amount, allowed_amount, rate }.
          const bulkByClient = {};
          bulkResponse.forEach(row => {
            if (row && row.client != null) {
              bulkByClient[Number(row.client)] = row;
            }
          });

          const lenders = debtClients.map((client, idx) => {
            const bulkRow = bulkByClient[Number(client.id)] || null;
            const maxAmount = bulkRow && bulkRow.max_amount != null
              ? Number(bulkRow.max_amount)
              : 0;
            const debtsForClient = teamDebts.filter(d => d.client === client.id);

            const principal = debtsForClient.reduce(
              (sum, d) => sum + (Number(d.balance) || 0),
              0
            );
            // On an active round, current-round interest is still
            // accruing; show prior_year_interest_payable (finalized
            // carry-in). On a closed round, interest_payable is final.
            //
            // Floor per row so the interest displayed on the lender
            // card matches the integer used as the repayment cap
            // (backend interest field carries decimal cents that
            // otherwise cause "exceeds outstanding" false-positives
            // when the user types the displayed whole-dollar total).
            const interestField = this.props.isActiveRound
              ? 'prior_year_interest_payable'
              : 'interest_payable';
            const interest = debtsForClient.reduce(
              (sum, d) => sum + Math.floor(Number(d[interestField]) || 0),
              0
            );
            // Pull the displayed interest rate from the team's debt record
            // for this lender so an in-flight loan shows the rate that was
            // locked in at borrow time. If the team has no debt with this
            // lender yet, fall back to the current offer rate from the
            // bulk response so the card still displays a live %.
            const bulkRate = bulkRow && bulkRow.rate != null
              ? Number(bulkRow.rate)
              : 0;
            const rate = debtsForClient.length
              ? Number(debtsForClient[0].rate || 0)
              : bulkRate;

            // D/C limit isn't returned by the bulk endpoint; fall back to
            // the lender preset (Prudent 40, Mezz 60, Great White 75) so
            // the progress-bar segments still render.
            const preset = pickPreset(client.name, idx);
            const dcLimit = preset.fallbackDcLimit || 0;

            // Track this round's debt record id (if any) so subsequent
            // borrow/repay clicks PUT (update) rather than POST (create
            // and hit the unique constraint on client/team/round).
            const { roundId } = this.props;
            const debtThisRound = roundId
              ? debtsForClient.find(d => Number(d.round) === Number(roundId))
              : null;

            // Per-lender amount math:
            //   currentRoundBorrow = how much was borrowed THIS round.
            //     Pulled from the debt record's `new_amount` field.
            //   prevRoundAmount   = the cumulative amount BEFORE this
            //     round. The backend's amount field on a debt row is
            //     prevRoundAmount + currentRoundBorrow.
            //
            // Used for two things:
            //  - Pre-filling the "Amount to Borrow This Round" input
            //    with what's already been borrowed this round.
            //  - Sending amount: prevRoundAmount + userInput on POST/PUT
            //    so the user's input represents the new total borrowed
            //    this round rather than an increment-on-top-of-existing.
            const currentRoundBorrow = debtThisRound
              ? Number(debtThisRound.new_amount || 0)
              : 0;
            let prevRoundAmount = 0;
            if (debtThisRound) {
              if (debtThisRound.prev_round_amount != null) {
                prevRoundAmount = Number(debtThisRound.prev_round_amount);
              } else {
                prevRoundAmount = Math.max(
                  0,
                  Number(debtThisRound.amount || 0) - currentRoundBorrow
                );
              }
            } else {
              const prior = debtsForClient
                .slice()
                .sort((a, b) => Number(b.round) - Number(a.round))[0];
              if (prior) {
                prevRoundAmount = Number(prior.amount || 0);
              }
            }

            // Look up any repayment records already posted this round.
            // Hoisted above the return so currentRoundInterestRepaid can
            // offset the displayed interest in `outstanding` — otherwise
            // Outstanding stays pinned at the starting prior-year value
            // even after the user paid down interest earlier this round.
            const dr = debtRepayments.find(r => r.client === client.id);
            const ip = interestPayments.find(r => r.client === client.id);
            const currentRoundInterestRepaid = ip ? Number(ip.amount || 0) : 0;
            const netInterest = Math.max(0, interest - currentRoundInterestRepaid);

            return {
              client_id: client.id,
              name: client.name,
              maxAmount: Number(maxAmount) || 0,
              rate: Number(rate) || 0,
              dcLimit, // 0..1 expected
              principal,
              interest: netInterest,
              outstanding: principal + netInterest,
              debtIdThisRound: debtThisRound ? debtThisRound.id : null,
              currentAmountThisRound: debtThisRound ? Number(debtThisRound.amount || debtThisRound.balance || 0) : 0,
              currentRoundBorrow,
              prevRoundAmount,
              // Existing repayment records for this lender + round.
              // Used by handlePostRepay to PUT (update) instead of
              // POST (create) on subsequent repayments this round,
              // and to know how much has already been allocated to
              // interest vs principal so we can keep adding.
              //
              // priorYearOutstanding = the "Outstanding" value shown
              // on the Repay panel header and the cap for repayments.
              // Sourced directly from the debt row for this round:
              //   prior_year_principal_balance + prior_year_interest_payable
              ...(() => {
                const priorPrincipal = debtThisRound
                  ? Number(debtThisRound.prior_year_principal_balance || 0) : 0;
                // Floor to match the interest displayed on the lender
                // card so validation against priorYearOutstanding
                // (used as the repayment cap) uses the same integer
                // the user sees.
                const priorInterest = debtThisRound
                  ? Math.floor(Number(debtThisRound.prior_year_interest_payable || 0)) : 0;
                return {
                  debtRepaymentId: dr ? dr.id : null,
                  interestRepaymentId: ip ? ip.id : null,
                  currentRoundPrincipalRepaid: dr ? Number(dr.amount || 0) : 0,
                  currentRoundInterestRepaid,
                  priorYearPrincipalBalance: priorPrincipal,
                  priorYearInterestPayable: priorInterest,
                  priorYearOutstanding: priorPrincipal + priorInterest
                };
              })()
            };
          });

          const totalExistingDebt = lenders.reduce(
            (sum, l) => sum + l.outstanding,
            0
          );

          // Net Capital Raised/Returned for debt = new borrowing this
          // round minus principal + interest repaid this round. Positive
          // means net inflow; negative means net outflow. All three
          // sources are already round-scoped (team-decisions and the
          // repayments/interest endpoints filter by round_number).
          const newDebtRaised = teamDebts.reduce(
            (sum, d) => sum + (Number(d.new_amount) || 0),
            0
          );
          const principalRepaid = debtRepayments.reduce(
            (sum, r) => sum + (Number(r.amount) || 0),
            0
          );
          const interestPaid = interestPayments.reduce(
            (sum, p) => sum + (Number(p.amount) || 0),
            0
          );
          const netCapital = newDebtRaised - (principalRepaid + interestPaid);

          const cashBalance = Number(financial.ending_cash) || 0;

          this.setState({
            loading: false,
            lenders,
            totalExistingDebt,
            netCapital,
            debtToCapital,
            cashBalance,
            playerId
          });
        });
      })
      .catch(() => {
        if (!this._isMounted) return;
        this.setState({ loading: false });
      });
  };

  refreshAfterPost = () => {
    const { roundNumber, roundId } = this.props;
    const apiUrl = process.env.REACT_APP_API_URL;
    const currentLenders = this.state.lenders;

    // Notify the parent so the Finance Decisions page can re-fetch its
    // KPIs (debt_to_capital, outstanding debt, etc.) — fired once per
    // successful POST/PUT inside this popup.
    if (typeof this.props.onDecisionMade === 'function') {
      this.props.onDecisionMade();
    }

    // Re-fetch team-decisions (for new balances + debt_to_capital)
    // alongside one bulk POST that returns max_amount + rate per
    // lender (replaces the previous N×2 per-lender calls).
    const bulkBody = {
      requests: currentLenders.map(lender => ({
        client: lender.client_id,
        term: 'LT',
        amount: 0,
      })),
    };

    return Promise.all([
      fetchTeamDecisions(roundNumber, "finance"),
      postRequest(`${apiUrl}static/debt_clients/bulk/`).send(bulkBody)
        .then(r => Array.isArray(r.body) ? r.body : [])
        .catch(() => []),
      getRequest(`${apiUrl}team/debt_repayments/?round_number=${roundNumber}`)
        .then(r => r.body).catch(() => []),
      getRequest(`${apiUrl}v2/finance/interest-payments/?round_number=${roundNumber}`)
        .then(r => r.body).catch(() => ({ results: [] }))
    ])
      .then(([decisions, bulkResponse, debtRepaymentsRes, interestPaymentsRes]) => {
        const debtRepayments = Array.isArray(debtRepaymentsRes)
          ? debtRepaymentsRes
          : (debtRepaymentsRes && debtRepaymentsRes.results) || [];
        const interestPayments = Array.isArray(interestPaymentsRes)
          ? interestPaymentsRes
          : (interestPaymentsRes && interestPaymentsRes.results) || [];
        return [decisions, bulkResponse, debtRepayments, interestPayments];
      })
      .then(([decisions, bulkResponse, debtRepayments, interestPayments]) => {
        if (!this._isMounted) return;
        const teamDebts = decisions.teamDebts || [];
        const financial = (decisions.financial_decisions && decisions.financial_decisions.team) || {};
        const debtToCapital = Number(financial.debt_to_capital || 0);
        const cashBalance = Number(financial.ending_cash) || 0;

        // Index the bulk response by client_id for O(1) lookup.
        const bulkByClient = {};
        bulkResponse.forEach(row => {
          if (row && row.client != null) {
            bulkByClient[Number(row.client)] = row;
          }
        });

        const lenders = currentLenders.map((lender) => {
          const debtsForClient = teamDebts.filter(d => d.client === lender.client_id);
          const principal = debtsForClient.reduce((sum, d) => sum + (Number(d.balance) || 0), 0);
          // Same active-round rule + floor as the initial load above.
          const interestFieldRefresh = this.props.isActiveRound
            ? 'prior_year_interest_payable'
            : 'interest_payable';
          const interest = debtsForClient.reduce((sum, d) => sum + Math.floor(Number(d[interestFieldRefresh]) || 0), 0);
          const bulkRow = bulkByClient[Number(lender.client_id)] || null;
          const bulkRate = bulkRow && bulkRow.rate != null ? Number(bulkRow.rate) : null;
          const rate = debtsForClient.length
            ? Number(debtsForClient[0].rate || 0)
            : (bulkRate != null ? bulkRate : lender.rate);

          // Refresh max_amount from the bulk response. Fall back to the
          // pre-refresh value if the bulk row is missing for any reason.
          const refreshedMax = bulkRow && bulkRow.max_amount != null
            ? Number(bulkRow.max_amount)
            : lender.maxAmount;

          // D/C limit isn't returned by the bulk endpoint; carry forward
          // the per-lender value already on state (seeded from the preset
          // at initial load).
          const newDcLimit = lender.dcLimit;

          // Recompute prevRoundAmount + currentRoundBorrow so the
          // next borrow click sends correct cumulative math and
          // the input pre-fill reflects the just-saved value.
          const debtThisRound = roundId
            ? debtsForClient.find(d => Number(d.round) === Number(roundId))
            : null;
          const currentRoundBorrow = debtThisRound
            ? Number(debtThisRound.new_amount || 0)
            : 0;
          let prevRoundAmount = 0;
          if (debtThisRound) {
            if (debtThisRound.prev_round_amount != null) {
              prevRoundAmount = Number(debtThisRound.prev_round_amount);
            } else {
              prevRoundAmount = Math.max(
                0,
                Number(debtThisRound.amount || 0) - currentRoundBorrow
              );
            }
          } else {
            const prior = debtsForClient
              .slice()
              .sort((a, b) => Number(b.round) - Number(a.round))[0];
            if (prior) {
              prevRoundAmount = Number(prior.amount || 0);
            }
          }

          const dr = debtRepayments.find(r => r.client === lender.client_id);
          const ip = interestPayments.find(r => r.client === lender.client_id);
          const priorPrincipal = debtThisRound
            ? Number(debtThisRound.prior_year_principal_balance || 0) : 0;
          // Floor to match displayed integer (see initial-load site).
          const priorInterest = debtThisRound
            ? Math.floor(Number(debtThisRound.prior_year_interest_payable || 0)) : 0;

          // Offset the displayed interest by any interest payments
          // already posted this round (mirrors the initial-load site).
          const currentRoundInterestRepaid = ip ? Number(ip.amount || 0) : 0;
          const netInterest = Math.max(0, interest - currentRoundInterestRepaid);

          return {
            ...lender,
            principal,
            interest: netInterest,
            outstanding: principal + netInterest,
            rate,
            maxAmount: Number(refreshedMax) || 0,
            dcLimit: newDcLimit,
            currentRoundBorrow,
            prevRoundAmount,
            debtIdThisRound: debtThisRound ? debtThisRound.id : null,
            currentAmountThisRound: debtThisRound ? Number(debtThisRound.amount || debtThisRound.balance || 0) : 0,
            debtRepaymentId: dr ? dr.id : null,
            interestRepaymentId: ip ? ip.id : null,
            currentRoundPrincipalRepaid: dr ? Number(dr.amount || 0) : 0,
            currentRoundInterestRepaid,
            priorYearPrincipalBalance: priorPrincipal,
            priorYearInterestPayable: priorInterest,
            priorYearOutstanding: priorPrincipal + priorInterest
          };
        });
        const totalExistingDebt = lenders.reduce((sum, l) => sum + l.outstanding, 0);
        const newDebtRaised = teamDebts.reduce(
          (sum, d) => sum + (Number(d.new_amount) || 0),
          0
        );
        const principalRepaid = debtRepayments.reduce(
          (sum, r) => sum + (Number(r.amount) || 0),
          0
        );
        const interestPaid = interestPayments.reduce(
          (sum, p) => sum + (Number(p.amount) || 0),
          0
        );
        const netCapital = newDebtRaised - (principalRepaid + interestPaid);
        this.setState({ lenders, totalExistingDebt, netCapital, debtToCapital, cashBalance });
      })
      .catch(() => {});
  };

  handleSelectLenderInternal = (lender) => {
    // Pre-populate the inputs with the existing this-round values so the
    // input's value tracks state directly. Falling back to the existing
    // amount only when state is empty caused a clear-to-empty backspace
    // to snap the input back to the pre-existing borrow amount.
    const existingBorrow = Math.round(Number(lender.currentRoundBorrow || 0));
    const existingRepay = Math.round(
      Number(lender.currentRoundPrincipalRepaid || 0) +
      Number(lender.currentRoundInterestRepaid || 0)
    );
    this.setState({
      selectedLenderId: lender.client_id,
      borrowAmount: existingBorrow > 0 ? String(existingBorrow) : '',
      repayAmount: existingRepay > 0 ? String(existingRepay) : '',
    });
  };

  handleChooseDifferentLender = () => {
    this.setState({ selectedLenderId: null, borrowAmount: '', repayAmount: '' });
  };

  handleBorrowAmountChange = (e) => {
    const val = e.target.value.replace(/[^\d]/g, '');
    this.setState({ borrowAmount: val });
  };

  handleRepayAmountChange = (e) => {
    const val = e.target.value.replace(/[^\d]/g, '');
    this.setState({ repayAmount: val });
  };

  showNotice = (title, message) => {
    this.setState({ noticeOpen: true, noticeTitle: title, noticeMessage: message });
  };

  hideNotice = () => {
    this.setState({ noticeOpen: false });
  };

  // Pull an error message out of an HTTP error. Looks at common shapes:
  //   - body.detail            (DRF default)
  //   - body.non_field_errors  (DRF validators)
  //   - any field-level error  (e.g. { amount: ["..."] })
  //   - plain string body
  // Returns null when nothing usable is found.
  extractBackendError = (err) => {
    const body = err && err.response && err.response.body;
    if (!body) return null;
    if (typeof body === 'string') return body;
    if (body.detail) return String(body.detail);
    if (Array.isArray(body.non_field_errors) && body.non_field_errors.length > 0) {
      return String(body.non_field_errors[0]);
    }
    if (body.message) return String(body.message);
    if (body.error) return String(body.error);
    for (const key of Object.keys(body)) {
      const val = body[key];
      if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string') {
        return `${key}: ${val[0]}`;
      }
      if (typeof val === 'string' && val.length > 0) {
        return `${key}: ${val}`;
      }
    }
    return null;
  };

  // Runs the same validation checks as handlePostBorrow but returns
  // {title, message} on failure so callers can surface the error
  // BEFORE showing a confirmation dialog. Returns null on success.
  getBorrowValidationError = () => {
    const { lenders, selectedLenderId, borrowAmount } = this.state;
    const lender = lenders.find(l => l.client_id === selectedLenderId);
    if (!lender) return { title: 'Error', message: 'No lender selected.' };

    const amount = Number(borrowAmount) || 0;
    if (amount < 0) {
      return { title: 'Invalid amount', message: 'Borrow amount cannot be negative.' };
    }
    // See handlePostBorrow for the delta-vs-total explanation.
    const currentRoundBorrow = Number(lender.currentRoundBorrow || 0);
    const isIncreasing = amount > currentRoundBorrow;
    const incrementalBorrow = amount - currentRoundBorrow;
    const maxAmt = Number(lender.maxAmount || 0);
    if (isIncreasing && incrementalBorrow > maxAmt) {
      const totalAllowed = currentRoundBorrow + maxAmt;
      return {
        title: 'Exceeds Available to Borrow',
        message: `The amount requested is in excess of what you can currently borrow from ${lender.name} given their debt-to-capital limits. You can borrow up to ${numberToM(totalAllowed)} in total this round based on your current debt-to-capital ratio.`,
      };
    }
    return null;
  };

  // Called from the BORROW button (POST only). Validates first so
  // errors surface immediately, then fetches the current interest
  // rate from the debt_conditions endpoint and opens the confirmation
  // modal with the actual rate. Bulk-response rate can be 0/missing
  // for some lenders — this call always returns the live rate for
  // the requested amount.
  requestBorrow = () => {
    const err = this.getBorrowValidationError();
    if (err) {
      this.showNotice(err.title, err.message);
      return;
    }
    const { lenders, selectedLenderId, borrowAmount } = this.state;
    const lender = lenders.find(l => l.client_id === selectedLenderId);
    if (!lender) return;

    const amount = Number(borrowAmount) || 0;
    const cumulativeAmount = Number(lender.prevRoundAmount || 0) + amount;
    // Seed the modal with the bulk rate (may be `--`) so it opens
    // immediately, then update in place once the live rate arrives.
    const seedRate = Number(lender.rate) > 0 ? Number(lender.rate) : null;
    this.setState({
      borrowConfirmOpen: true,
      borrowConfirmRate: seedRate,
      borrowConfirmLoadingRate: true,
    });

    const apiUrl = process.env.REACT_APP_API_URL;
    postRequest(
      `${apiUrl}static/debt_clients/${lender.client_id}/get_debt_conditions/`
    )
      .send({ amount: cumulativeAmount, term: 8 })
      .then(res => {
        if (!this._isMounted) return;
        const body = (res && res.body) || {};
        const liveRate = Number(body.rate);
        this.setState({
          borrowConfirmRate: liveRate > 0 ? liveRate : seedRate,
          borrowConfirmLoadingRate: false,
        });
      })
      .catch(() => {
        // Fetch failed — leave whatever seed rate we already have
        // and stop the loading indicator. Confirm still works.
        if (!this._isMounted) return;
        this.setState({ borrowConfirmLoadingRate: false });
      });
  };

  cancelBorrowConfirm = () => {
    this.setState({
      borrowConfirmOpen: false,
      borrowConfirmRate: null,
      borrowConfirmLoadingRate: false,
    });
  };

  confirmBorrow = () => {
    this.setState({
      borrowConfirmOpen: false,
      borrowConfirmRate: null,
      borrowConfirmLoadingRate: false,
    }, () => {
      this.handlePostBorrow();
    });
  };

  handlePostBorrow = () => {
    const { lenders, selectedLenderId, borrowAmount } = this.state;
    const lender = lenders.find(l => l.client_id === selectedLenderId);
    if (!lender) return;

    const amount = Number(borrowAmount) || 0;
    if (amount < 0) {
      this.showNotice('Invalid amount', 'Borrow amount cannot be negative.');
      return;
    }
    // Only validate Max Loan when the user is INCREASING this round's
    // borrowing. If they're decreasing (or leaving the same) the
    // existing `new_amount`, they're just modifying an existing loan
    // and the lender's current cap is not relevant.
    //
    // maxAmount is the REMAINING capacity from the backend, computed
    // after this round's already-booked borrow. So compare the DELTA
    // (new total minus already-borrowed-this-round) against maxAmount,
    // not the new total — otherwise editing a $2M borrow up to $3M
    // reads as "$3M > $1M remaining" even though the $1M delta fits.
    const currentRoundBorrow = Number(lender.currentRoundBorrow || 0);
    const isIncreasing = amount > currentRoundBorrow;
    const incrementalBorrow = amount - currentRoundBorrow;
    const maxAmt = Number(lender.maxAmount || 0);
    if (isIncreasing && incrementalBorrow > maxAmt) {
      const totalAllowed = currentRoundBorrow + maxAmt;
      this.showNotice(
        'Exceeds Available to Borrow',
        `The amount requested is in excess of what you can currently borrow from ${lender.name} given their debt-to-capital limits. You can borrow up to ${numberToM(totalAllowed)} in total this round based on your current debt-to-capital ratio.`
      );
      return;
    }

    this.setState({ postingBorrow: true });
    const apiUrl = process.env.REACT_APP_API_URL;
    const { roundId } = this.props;

    // The DB has a unique constraint on (client, team, round). If a
    // debt record already exists for this lender this round, PUT to
    // update it; otherwise POST to create.
    const request = lender.debtIdThisRound
      ? putRequest(`${apiUrl}team/debts/${lender.debtIdThisRound}/`)
      : postRequest(`${apiUrl}team/debts/`);

    // Per backend contract: the `amount` field is the cumulative
    // total loan amount = prior round's amount + the user's new
    // "Amount to Borrow This Round" input (which represents the
    // total borrowed this round, not an increment). The backend
    // validator rejects writes where amount < the prior round's
    // debt, so always send prevRoundAmount + input.
    const cumulativeAmount = Number(lender.prevRoundAmount || 0) + amount;

    request
      .send({
        client: lender.client_id,
        round: roundId,
        amount: cumulativeAmount,
        new_amount: amount,
        term: 8
      })
      .then(() => {
        if (!this._isMounted) return;
        this.refreshAfterPost().then(() => {
          if (!this._isMounted) return;
          // Keep borrowAmount in sync with the just-saved value so the
          // input stays populated with what's borrowed this round.
          this.setState({ postingBorrow: false });
        });
      })
      .catch(err => {
        if (!this._isMounted) return;
        const msg = this.extractBackendError(err) ||
          'Unable to post the borrowing decision. Please try again.';
        this.setState({ postingBorrow: false });
        this.showNotice('Post Failed', msg);
      });
  };

  handlePostRepay = () => {
    const { lenders, selectedLenderId, repayAmount, cashBalance } = this.state;
    const lender = lenders.find(l => l.client_id === selectedLenderId);
    if (!lender) return;

    const amount = Number(repayAmount) || 0;
    if (amount < 0) {
      this.showNotice('Invalid amount', 'Repayment amount cannot be negative.');
      return;
    }
    // Block repayment when the team has also borrowed from the same
    // lender this round. The team's intent in that moment is to reduce
    // their total exposure to this lender — the right lever is to
    // lower this round's borrow amount, not to post a repayment on top.
    // Repayments only apply to prior-round balances, so we steer them
    // back to the Borrow input. Clearing an existing repayment (amount
    // = 0) is still allowed since that's a cancellation, not a new
    // repayment.
    const currentRoundBorrow = Number(lender.currentRoundBorrow) || 0;
    if (amount > 0 && currentRoundBorrow > 0) {
      this.showNotice(
        'New loan active',
        `You can't repay ${lender.name} while a new loan from them is active this round. To reduce your total loan, lower this round's borrow amount instead. Once the new loan is set to $0, you can repay prior balances.`
      );
      return;
    }
    // Only enforce caps when the user is INCREASING this round's
    // repayment. Decreasing an existing repayment frees up cash and
    // can't push outstanding above its already-accepted value.
    const existingRepayment =
      (Number(lender.currentRoundPrincipalRepaid) || 0) +
      (Number(lender.currentRoundInterestRepaid) || 0);
    const isIncreasing = amount > existingRepayment;

    // Cap the repayment at the "Outstanding" value shown on the
    // Repay panel header (prior_year_principal_balance +
    // prior_year_interest_payable). Backend rejects anything more.
    if (isIncreasing && amount > Number(lender.priorYearOutstanding || 0)) {
      this.showNotice(
        'Exceeds Outstanding Value',
        `The value entered is in excess of the outstanding value. ${lender.name}'s Outstanding is ${numberToM(lender.priorYearOutstanding)}.`
      );
      return;
    }
    // Only the delta from what's already been paid this round hits
    // cash — the prior payment was deducted from cashBalance when it
    // was posted, so a PUT that increases the total by X only needs X
    // in available cash, not the full new total.
    const additionalCashNeeded = Math.max(0, amount - existingRepayment);
    if (isIncreasing && additionalCashNeeded > Number(cashBalance || 0)) {
      this.showNotice('Insufficient Cash',
        `Your available cash balance is ${numberToM(cashBalance)}. Reduce the repayment amount or raise additional capital first.`);
      return;
    }

    this.setState({ postingRepay: true });
    const apiUrl = process.env.REACT_APP_API_URL;

    // Split the user's input: pay outstanding interest first, then
    // any remaining to principal. The caps here must match the
    // "Outstanding" header (prior-year fields) — the backend
    // interest-payments endpoint rejects amounts > prior_year_interest_payable
    // and debt_repayments rejects amounts > prior_year_principal_balance.
    //
    // Floor the interest portion to a whole dollar — the interest-payments
    // API expects integer amounts (no cents). Any sub-dollar residual on
    // prior_year_interest_payable rolls forward instead of being paid.
    // Principal is derived from the floored interest so the user's total
    // repayment intent is preserved (the "lost" cents go to principal).
    const interestOutstanding = Math.max(0, Number(lender.priorYearInterestPayable || 0));
    const principalOutstanding = Math.max(0, Number(lender.priorYearPrincipalBalance || 0));
    const interestPortion = Math.floor(Math.min(amount, interestOutstanding));
    const principalPortion = Math.min(
      Math.max(0, amount - interestPortion),
      principalOutstanding
    );

    const requests = [];

    // Interest. Always PUT an existing record (so the user can
    // reset a prior payment to 0). Only POST a new record when the
    // portion is > 0 — no point creating a zero-amount record.
    if (lender.interestRepaymentId || interestPortion > 0) {
      const req = lender.interestRepaymentId
        ? putRequest(`${apiUrl}v2/finance/interest-payments/${lender.interestRepaymentId}/`)
        : postRequest(`${apiUrl}v2/finance/interest-payments/`);
      requests.push(
        req.send({
          client: lender.client_id,
          amount: interestPortion,
          debt: lender.debtIdThisRound,
          player: this.state.playerId,
          round: this.props.roundId,
          team: Number(Auth.getTeamId()),
          term: 8
        })
      );
    }

    // Principal. Same pattern — PUT existing even at 0, POST only
    // when there's actually a principal payment.
    if (lender.debtRepaymentId || principalPortion > 0) {
      const req = lender.debtRepaymentId
        ? putRequest(`${apiUrl}team/debt_repayments/${lender.debtRepaymentId}/`)
        : postRequest(`${apiUrl}team/debt_repayments/`);
      requests.push(
        req.send({
          client: lender.client_id,
          term: 8,
          amount: principalPortion
        })
      );
    }

    Promise.all(requests)
      .then(() => {
        if (!this._isMounted) return;
        this.refreshAfterPost().then(() => {
          if (!this._isMounted) return;
          this.setState({ postingRepay: false, repayAmount: '' });
        });
      })
      .catch(err => {
        if (!this._isMounted) return;
        const msg = this.extractBackendError(err) ||
          'Unable to post the repayment decision. Please try again.';
        this.setState({ postingRepay: false });
        this.showNotice('Post Failed', msg);
      });
  };

  renderLenderCard = (lender, idx, opts) => {
    const { roundNumber } = this.props;
    const preset = pickPreset(lender.name, idx);
    const { withAnimation = true, hideSelectButton = false, showOutstanding = true } = opts || {};

    // Per-lender round activity sum drives the section header verb:
    //   sum = debts.new_amount − debt_repayments.amount − interest_payments.amount
    // ≥ 0 → "Amount Borrowed Year N"; < 0 → "Amount Repaid Year N".
    // The big numeric value is the signed sum; a negative one renders red.
    const borrowedThisRound = Number(lender.currentRoundBorrow) || 0;
    const principalRepaidThisRound = Number(lender.currentRoundPrincipalRepaid) || 0;
    const interestPaidThisRound = Number(lender.currentRoundInterestRepaid) || 0;
    const amountSign = borrowedThisRound - principalRepaidThisRound - interestPaidThisRound;
    const amountVerb = amountSign >= 0 ? 'Borrowed' : 'Repaid';
    return (
      <Styled.LenderCard
        key={lender.client_id}
        theme={preset.theme}
        index={withAnimation ? idx : 0}
        noAnimation={!withAnimation}
      >
        <Styled.LenderCardTop>
          <Styled.LenderLogo>
            <LenderArtFrame>{renderLenderArt(preset.iconKey)}</LenderArtFrame>
          </Styled.LenderLogo>
          <Styled.LenderTitleArea>
            <Styled.LenderName>{lender.name}</Styled.LenderName>
            {preset.tagline && <Styled.LenderTagline>{preset.tagline}</Styled.LenderTagline>}
            <Styled.LenderRiskBadge theme={preset.theme}>{preset.risk}</Styled.LenderRiskBadge>
          </Styled.LenderTitleArea>
        </Styled.LenderCardTop>

        {preset.description && (
          <Styled.LenderDescription>{preset.description}</Styled.LenderDescription>
        )}

        <Styled.LenderInnerCard>
          <Styled.LenderInnerLabel>Lender Offer</Styled.LenderInnerLabel>
          <Styled.LenderOfferRow>
            <Styled.LenderOfferCol>
              <Styled.LenderOfferLabel>
                Max Loan{' '}
                <LenderInfoWrapper>
                  <Styled.LenderCardInfoIcon>i</Styled.LenderCardInfoIcon>
                  <LenderInfoTooltip className="lender-info-tooltip">
                    The most this lender will offer right now, based on your
                    debt-to-capital ratio. Lower your ratio and you can borrow
                    more.
                  </LenderInfoTooltip>
                </LenderInfoWrapper>
              </Styled.LenderOfferLabel>
              <Styled.LenderOfferValue theme={preset.theme}>
                <CountUp value={lender.maxAmount} format={formatFullDollar} />
              </Styled.LenderOfferValue>
            </Styled.LenderOfferCol>
            <Styled.LenderOfferCol>
              <Styled.LenderOfferLabel>Interest Rate</Styled.LenderOfferLabel>
              <Styled.LenderOfferValue theme={preset.theme}>
                {(() => {
                  if (Number(lender.rate) > 0) {
                    return `${(lender.rate * 100).toFixed(1)}%`;
                  }
                  if (preset.theme === 'green') return 'Low';
                  if (preset.theme === 'yellow') return 'Moderate';
                  if (preset.theme === 'red') return 'High';
                  return '--';
                })()}
              </Styled.LenderOfferValue>
            </Styled.LenderOfferCol>
            <Styled.LenderOfferCol>
              <Styled.LenderOfferLabel>
                D/C Limit{' '}
                <LenderInfoWrapper>
                  <Styled.LenderCardInfoIcon>i</Styled.LenderCardInfoIcon>
                  <LenderInfoTooltip className="lender-info-tooltip">
                    The debt-to-capital ratio this lender won't let you cross.
                    Stay below it and they'll keep lending to you.
                  </LenderInfoTooltip>
                </LenderInfoWrapper>
              </Styled.LenderOfferLabel>
              <Styled.LenderOfferValue theme={preset.theme}>{(lender.dcLimit * 100).toFixed(0)}%</Styled.LenderOfferValue>
            </Styled.LenderOfferCol>
          </Styled.LenderOfferRow>
        </Styled.LenderInnerCard>

        {showOutstanding && (
          <Styled.LenderInnerCard>
            <Styled.LenderInnerLabel>{`Net Amount ${amountVerb} Year ${roundNumber}`}</Styled.LenderInnerLabel>
            <Styled.LenderBalanceBig
              theme={preset.theme}
              style={amountSign < 0 ? { color: '#FE7474' } : undefined}
            >
              <CountUp value={amountSign} format={formatFullDollar} />
            </Styled.LenderBalanceBig>
            <Styled.LenderBalanceSplit>
              <Styled.LenderBalanceSplitCol>
                <Styled.LenderBalanceSplitLabel>Outstanding Balance</Styled.LenderBalanceSplitLabel>
                <Styled.LenderBalanceSplitValue theme={preset.theme}>
                  <CountUp value={lender.outstanding} format={formatFullDollar} />
                </Styled.LenderBalanceSplitValue>
              </Styled.LenderBalanceSplitCol>
            </Styled.LenderBalanceSplit>
          </Styled.LenderInnerCard>
        )}

        {!hideSelectButton && (
          <Styled.LenderSelectButton
            theme={preset.theme}
            onClick={() => this.handleSelectLenderInternal(lender)}
          >
            Select
          </Styled.LenderSelectButton>
        )}
      </Styled.LenderCard>
    );
  };

  renderManageLoanPanel = (lender) => {
    const preset = pickPreset(lender.name, 0);
    const { borrowAmount, repayAmount, postingBorrow, postingRepay, cashBalance } = this.state;
    const { roundNumber } = this.props;
    const showRepaySection = Number(cashBalance) >= 0 && Number(roundNumber) > 1;

    const existingBorrow = Math.round(Number(lender.currentRoundBorrow || 0));
    const hasExistingBorrow = existingBorrow > 0;
    const borrowDisplay = borrowAmount !== ''
      ? Number(borrowAmount).toLocaleString()
      : '';
    const borrowChanged = borrowAmount !== '' && Number(borrowAmount) !== existingBorrow;

    const existingRepay = Math.round(
      Number(lender.currentRoundPrincipalRepaid || 0) +
      Number(lender.currentRoundInterestRepaid || 0)
    );
    const hasExistingRepay = existingRepay > 0;
    const repayDisplay = repayAmount !== ''
      ? Number(repayAmount).toLocaleString()
      : '';
    const repayChanged = repayAmount !== '' && Number(repayAmount) !== existingRepay;

    return (
      <Styled.ManageLoanPanel>
        <Styled.ManageLoanTitle>Manage Your Loan</Styled.ManageLoanTitle>
        <Styled.ManageLoanSubtitle>
          Set how much you'd like to borrow this round and manage any prior balances.
        </Styled.ManageLoanSubtitle>

        <Styled.ManageLoanSection theme={preset.theme}>
          <Styled.ManageSectionTitle>
            <span>Amount to Borrow This Round</span>
          </Styled.ManageSectionTitle>
          <Styled.ManageSectionDescription>
            {lender.name} can lend up to {numberToM(lender.maxAmount)} based on your current debt-to-capital ratio.
          </Styled.ManageSectionDescription>
          <Styled.ManageInputRow>
            <Styled.ManageAmountField>
              <Styled.ManageAmountPrefix>$</Styled.ManageAmountPrefix>
              <Styled.ManageAmountInput
                type="text"
                inputMode="numeric"
                value={borrowDisplay}
                onChange={this.handleBorrowAmountChange}
                placeholder="0"
              />
            </Styled.ManageAmountField>
            {hasExistingBorrow ? (
              <Styled.ManageUpdateButton
                onClick={this.handlePostBorrow}
                disabled={postingBorrow || !borrowChanged}
              >
                {postingBorrow ? 'UPDATING...' : 'UPDATE'}
              </Styled.ManageUpdateButton>
            ) : (
              <Styled.ManagePostButton
                theme={preset.theme}
                onClick={this.requestBorrow}
                disabled={postingBorrow}
              >
                {postingBorrow ? 'BORROWING...' : 'BORROW'}
              </Styled.ManagePostButton>
            )}
          </Styled.ManageInputRow>
          <Styled.ManageMetaRow>
            <Styled.ManageMetaLabel>Available to Borrow:</Styled.ManageMetaLabel>
            <Styled.ManageMetaValue>
              <CountUp value={lender.maxAmount} format={numberToM} />
            </Styled.ManageMetaValue>
          </Styled.ManageMetaRow>
        </Styled.ManageLoanSection>

        {showRepaySection && (
          <Styled.ManageLoanSection theme={preset.theme}>
            <Styled.ManageSectionTitle>
              <span>Repay Prior Year Balance</span>
              <span style={{ fontSize: 14, color: 'rgba(0,27,59,0.6)' }}>
                OUTSTANDING <strong style={{ color: '#001B3B', marginLeft: 8 }}>
                  <CountUp value={lender.priorYearOutstanding} format={numberToM} />
                </strong>
              </span>
            </Styled.ManageSectionTitle>
            <Styled.ManageSectionDescription>
              Pay down any outstanding balance from previous rounds to reduce your interest burden.
            </Styled.ManageSectionDescription>
            <Styled.ManageInputRow>
              <Styled.ManageAmountField>
                <Styled.ManageAmountPrefix>$</Styled.ManageAmountPrefix>
                <Styled.ManageAmountInput
                  type="text"
                  inputMode="numeric"
                  value={repayDisplay}
                  onChange={this.handleRepayAmountChange}
                  placeholder="0"
                />
              </Styled.ManageAmountField>
              {hasExistingRepay ? (
                <Styled.ManageUpdateButton
                  onClick={this.handlePostRepay}
                  disabled={postingRepay || !repayChanged}
                >
                  {postingRepay ? 'UPDATING...' : 'UPDATE'}
                </Styled.ManageUpdateButton>
              ) : (
                <Styled.ManagePostButton
                  theme={preset.theme}
                  onClick={this.handlePostRepay}
                  disabled={postingRepay}
                >
                  {postingRepay ? 'REPAYING...' : 'REPAY'}
                </Styled.ManagePostButton>
              )}
            </Styled.ManageInputRow>
            <Styled.ManageMetaRow>
              <Styled.ManageMetaLabel>Your Available Cash Balance:</Styled.ManageMetaLabel>
              <Styled.ManageMetaValue>
                <CountUp value={cashBalance} format={formatFullDollar} />
              </Styled.ManageMetaValue>
            </Styled.ManageMetaRow>
          </Styled.ManageLoanSection>
        )}
      </Styled.ManageLoanPanel>
    );
  };

  render() {
    const { onClose } = this.props;
    const { loading, lenders, totalExistingDebt, netCapital, debtToCapital, selectedLenderId, noticeOpen, noticeTitle, noticeMessage } = this.state;
    const selectedLender = selectedLenderId
      ? lenders.find(l => l.client_id === selectedLenderId)
      : null;

    // Build progress bar segments based on lender D/C limits, sorted ascending.
    // Each lender's limit is a threshold; the bar shows green up to lender 1's
    // limit, yellow to lender 2's, red to lender 3's, dark beyond.
    const sortedByLimit = [...lenders].sort((a, b) => a.dcLimit - b.dcLimit);
    const segmentColorKeys = ["green", "yellow", "red"];

    const currentRatioPct = Math.min(100, Math.max(0, debtToCapital * 100));

    // Build segments cumulative widths up to 100%
    let prevLimit = 0;
    const segments = [];
    sortedByLimit.forEach((lender, idx) => {
      const limitPct = Math.min(100, lender.dcLimit * 100);
      const width = Math.max(0, limitPct - prevLimit);
      segments.push({
        color: SEGMENT_COLORS[segmentColorKeys[idx] || "red"],
        width,
        labelPct: limitPct,
        labelText: `${lender.name.split(" ")[0]} ${limitPct.toFixed(0)}%`,
        labelColor: SEGMENT_COLORS[segmentColorKeys[idx] || "red"]
      });
      prevLimit = limitPct;
    });
    if (prevLimit < 100) {
      segments.push({
        color: SEGMENT_COLORS.black,
        width: 100 - prevLimit
      });
    }

    return (
      <Styled.LenderOverlay>
        <Styled.LenderModalHeader>
          <Styled.LenderModalTitle>Choose Your Lender</Styled.LenderModalTitle>
          <Styled.LenderModalSubtitle>
            Select one or more lenders to fund your business. Each lender comes with different terms. The higher your debt to capital ratio, the less you can borrow.
          </Styled.LenderModalSubtitle>
          <Styled.LenderModalClose onClick={onClose} aria-label="Close">
            x
          </Styled.LenderModalClose>
        </Styled.LenderModalHeader>

        {loading && (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Loader type="ball-scale-ripple" active />
          </div>
        )}

        {!loading && (
          <div>
            <Styled.LenderStatusBar>
              <Styled.LenderStatusLeft>
                {/* Net Capital Raised/Returned = sum(debts.new_amount)
                    - (sum(debt_repayments.amount) + sum(interest_payments.amount)).
                    Positive value = net inflow this round; negative = net
                    outflow. Red when negative to mirror the rest of the
                    "cash used" treatment. */}
                <Styled.LenderStatusItem divider>
                  <Styled.LenderStatusLabel>Net Capital Raised/Returned</Styled.LenderStatusLabel>
                  <Styled.LenderStatusValue style={{ color: netCapital < 0 ? '#C62828' : undefined }}>
                    <CountUp value={netCapital} format={numberToM} />
                  </Styled.LenderStatusValue>
                </Styled.LenderStatusItem>
                <Styled.LenderStatusItem>
                  <Styled.LenderStatusLabel>Your Existing Debt</Styled.LenderStatusLabel>
                  <Styled.LenderStatusValue>
                    <CountUp value={totalExistingDebt} format={numberToM} />
                  </Styled.LenderStatusValue>
                </Styled.LenderStatusItem>
              </Styled.LenderStatusLeft>
              <Styled.LenderStatusRight>
                <Styled.LenderStatusRightLabel>Your Current Debt / Capital Ratio</Styled.LenderStatusRightLabel>
                <Styled.LenderRatioBar>
                  {segments.map((seg, i) => (
                    <Styled.LenderRatioSegment key={i} color={seg.color} width={seg.width}>
                      {seg.labelText && (
                        <Styled.LenderRatioMarker color={seg.labelColor} position={100}>
                          {seg.labelText}
                        </Styled.LenderRatioMarker>
                      )}
                    </Styled.LenderRatioSegment>
                  ))}
                  <RatioDotWrapper style={{ left: `${currentRatioPct}%` }}>
                    <RatioDotVisual />
                    <RatioTooltip className="ratio-tooltip">
                      {(debtToCapital * 100).toFixed(2)}%
                    </RatioTooltip>
                  </RatioDotWrapper>
                </Styled.LenderRatioBar>
              </Styled.LenderStatusRight>
            </Styled.LenderStatusBar>

            {(() => {
              // Show the "Amount Borrowed/Repaid" section on every card
              // when any lender has either an outstanding balance OR any
              // round activity (borrow / repayment / interest payment).
              // This way a round whose only activity was a full payoff
              // still surfaces the "Amount Repaid Year N" header.
              const anyOutstanding = lenders.some(l =>
                Number(l.outstanding) > 0
                || Number(l.currentRoundBorrow) !== 0
                || Number(l.currentRoundPrincipalRepaid) !== 0
                || Number(l.currentRoundInterestRepaid) !== 0
              );
              return (
              <div>
            {!selectedLender && (
              <Styled.LenderGrid>
                {lenders.map((lender, idx) => this.renderLenderCard(lender, idx, { showOutstanding: anyOutstanding }))}
              </Styled.LenderGrid>
            )}

            {selectedLender && (
              <Styled.LenderSplit>
                <div>
                  <Styled.SelectedLenderWrapper>
                    {this.renderLenderCard(selectedLender, 0, { withAnimation: false, hideSelectButton: true, showOutstanding: anyOutstanding })}
                  </Styled.SelectedLenderWrapper>
                  <Styled.ChooseDifferentLenderRow>
                    <Styled.ChooseDifferentLenderButton onClick={this.handleChooseDifferentLender}>
                      Choose a different Lender
                    </Styled.ChooseDifferentLenderButton>
                  </Styled.ChooseDifferentLenderRow>
                </div>
                {this.renderManageLoanPanel(selectedLender)}
              </Styled.LenderSplit>
            )}
              </div>
              );
            })()}
          </div>
        )}

        {noticeOpen && (() => {
          const isNoCeoProjections =
            typeof noticeMessage === 'string'
            && /no ceo projections/i.test(noticeMessage);
          const handleAction = () => {
            this.hideNotice();
            if (isNoCeoProjections && typeof this.props.onGoToCeoProjections === 'function') {
              this.props.onGoToCeoProjections();
            }
          };
          const displayMessage = isNoCeoProjections
            ? "Before you can raise capital, the CEO needs to submit financial projections for this round. Lenders use those projections to decide how much they'll offer. Head to CEO Decisions to enter them, then come back to borrow."
            : noticeMessage;
          return (
            <Styled.LenderNoticeOverlay onClick={this.hideNotice}>
              <Styled.LenderNoticeModal onClick={e => e.stopPropagation()}>
                <Styled.LenderNoticeTitle>{noticeTitle}</Styled.LenderNoticeTitle>
                <Styled.LenderNoticeMessage>{displayMessage}</Styled.LenderNoticeMessage>
                <Styled.LenderNoticeActions>
                  <Styled.LenderNoticeButton onClick={handleAction}>
                    {isNoCeoProjections ? 'Check CEO Projections' : 'OK'}
                  </Styled.LenderNoticeButton>
                </Styled.LenderNoticeActions>
              </Styled.LenderNoticeModal>
            </Styled.LenderNoticeOverlay>
          );
        })()}

        {this.state.borrowConfirmOpen && (() => {
          const lender = (this.state.lenders || []).find(
            l => l.client_id === this.state.selectedLenderId
          );
          const amount = Number(this.state.borrowAmount) || 0;
          // Prefer the live rate fetched from debt_conditions when
          // the user clicked BORROW. Fall back to the lender's card
          // rate (from the bulk endpoint) if the fetch hasn't
          // returned yet AND the card had a positive value.
          const fetchedRate = this.state.borrowConfirmRate;
          const cardRate = lender ? Number(lender.rate || 0) : 0;
          const rate = fetchedRate != null
            ? Number(fetchedRate)
            : cardRate;
          const rateLabel = this.state.borrowConfirmLoadingRate && fetchedRate == null
            ? 'Loading…'
            : (rate > 0 ? `${(rate * 100).toFixed(1)}%` : '--');
          return (
            <Styled.LenderNoticeOverlay onClick={this.cancelBorrowConfirm}>
              <Styled.LenderNoticeModal onClick={e => e.stopPropagation()}>
                <Styled.LenderNoticeTitle>Confirm Loan Terms</Styled.LenderNoticeTitle>
                <Styled.LenderNoticeMessage>
                  Review the loan terms below before you commit.
                  Once accepted, {lender ? lender.name : 'this lender'} will hold the loan on your books until you repay it.
                </Styled.LenderNoticeMessage>
                <Styled.LenderNoticeTermsList>
                  <Styled.LenderNoticeTermLabel>Lender</Styled.LenderNoticeTermLabel>
                  <Styled.LenderNoticeTermValue>
                    {lender ? lender.name : '--'}
                  </Styled.LenderNoticeTermValue>
                  <Styled.LenderNoticeTermLabel>Loan Amount</Styled.LenderNoticeTermLabel>
                  <Styled.LenderNoticeTermValue>
                    ${amount.toLocaleString('en-US')}
                  </Styled.LenderNoticeTermValue>
                  <Styled.LenderNoticeTermLabel>Interest Rate</Styled.LenderNoticeTermLabel>
                  <Styled.LenderNoticeTermValue>{rateLabel}</Styled.LenderNoticeTermValue>
                  <Styled.LenderNoticeTermLabel>Maturity</Styled.LenderNoticeTermLabel>
                  <Styled.LenderNoticeTermValue>8 years</Styled.LenderNoticeTermValue>
                </Styled.LenderNoticeTermsList>
                <Styled.LenderNoticeActions>
                  <Styled.LenderNoticeCancelButton onClick={this.cancelBorrowConfirm}>
                    Cancel
                  </Styled.LenderNoticeCancelButton>
                  <Styled.LenderNoticeButton onClick={this.confirmBorrow}>
                    Accept
                  </Styled.LenderNoticeButton>
                </Styled.LenderNoticeActions>
              </Styled.LenderNoticeModal>
            </Styled.LenderNoticeOverlay>
          );
        })()}
      </Styled.LenderOverlay>
    );
  }
}

export default DebtDecisionsPage;



// WEBPACK FOOTER //
// ./src/components/finance/decisions/debt-decisions-page.js